import defaultCompanyLogoUrl from "~/images/default_company_logo.svg"
import { Tag, TagIcon, TagLabel } from "@/react/ui/tag"
import { Icon } from "@/react/ui/icon"
import { Separator } from "@/react/ui/separator"
import { Button } from "@/react/ui/button"
import {
  KeyValue,
  KeyValueDescription,
  KeyValueItem,
  KeyValueTitle,
} from "@/react/ui/key-value"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsListItem,
  TabsTrigger,
} from "@/react/ui/tabs"
import DomainSidepanelEmails from "~/react/components/domain_sidepanel_emails"
import { useCampaigns } from "~/react/components/campaigns-context"
import { useLeadsLists } from "~/react/components/leads-lists-context"
import { DomainSidepanelTechnologies } from "~/react/components/domain_sidepanel_technologies"
import { DiscoverDomain } from "~/react/types/discover"
import { useState } from "react"
import { DomainSidepanelSignals } from "~/react/components/domain_sidepanel_signals"
import { CompanySaveManager } from "../company-save-manager"
import { useCompanyLists } from "../company-lists-context"
import { useCompanySave } from "@/react/hooks/useCompanySave"

const TRANSLATION_PREFIX = "js.discover.company_details"

export const CompanyDetails = ({
  domain,
  numberOfCreditsLeft,
  singleCreditsBucket,
}: {
  domain: DiscoverDomain
  numberOfCreditsLeft: number
  singleCreditsBucket: boolean
}) => {
  const [logoUrl, setLogoUrl] = useState<string>(
    `https://logos.hunter.io/${domain.value}`,
  )
  const { leadsLists } = useLeadsLists()
  const { campaigns } = useCampaigns()
  const { companyLists, setCompanyLists } = useCompanyLists()
  const { handleCompanySave } = useCompanySave(companyLists, setCompanyLists)

  const handleSaveCompanies = async (_name: string, listId: number | null) => {
    await handleCompanySave([domain.id], listId || undefined)
  }

  return (
    <div>
      <Tabs defaultValue="email_addresses" className="mt-4">
        <section className="lead-details">
          <header className="lead-details__header">
            <figure className="lead-details__icon">
              {logoUrl !== defaultCompanyLogoUrl && (
                <img
                  src={logoUrl}
                  alt={domain.companyName}
                  className="lead-details__icon-bg"
                />
              )}
              <img
                src={logoUrl}
                alt={domain.companyName}
                className="lead-details__icon-logo"
                onError={() => setLogoUrl(defaultCompanyLogoUrl)}
              />
            </figure>

            <div className="lead-details__heading">
              <h2 className="lead-details__name">{domain.companyName}</h2>
              <p className="lead-details__baseline"></p>
              <div className="lead-details__metas">
                <Tag>
                  <TagLabel>
                    {i18n.t(`${TRANSLATION_PREFIX}.email_addresses_count`, {
                      count: domain.emailsCount,
                    })}
                  </TagLabel>
                </Tag>
                {domain.recentlyRaisedFunds && (
                  <Tag variant="ai">
                    <TagIcon>
                      <Icon icon="sparkle" />
                    </TagIcon>
                    <TagLabel>
                      {i18n.t(`${TRANSLATION_PREFIX}.recently_raised_funds`)}
                    </TagLabel>
                  </Tag>
                )}
                {domain.currentlyHiring && (
                  <Tag variant="ai">
                    <TagIcon>
                      <Icon icon="sparkle" />
                    </TagIcon>
                    <TagLabel>
                      {i18n.t(`${TRANSLATION_PREFIX}.currently_hiring`)}
                    </TagLabel>
                  </Tag>
                )}
              </div>
            </div>
          </header>
          {domain.description && (
            <h3 className="lead-details__description">{domain.description}</h3>
          )}
          <CompanySaveManager
            id={domain.id.toString()}
            companyLists={companyLists}
            setCompanyLists={setCompanyLists}
            onSave={handleSaveCompanies}
            disableButtons={false}
            count={1}
          >
            <Button size="sm" className="mt-4">
              <Icon icon="list" />
              {i18n.t(`${TRANSLATION_PREFIX}.add_to_list`)}
              <Icon icon="chevron-down" />
            </Button>
          </CompanySaveManager>
          <Separator />
          <KeyValue columns>
            <KeyValueItem>
              <KeyValueTitle>
                {i18n.t(`${TRANSLATION_PREFIX}.company_details`)}
              </KeyValueTitle>
              <KeyValueDescription>{domain.industryName}</KeyValueDescription>
              <KeyValueDescription>
                {i18n.t(`${TRANSLATION_PREFIX}.employees`, {
                  count: domain.size,
                })}
              </KeyValueDescription>
              {domain.foundedYear && (
                <KeyValueDescription>
                  {i18n.t(`${TRANSLATION_PREFIX}.founded_in`, {
                    year: domain.foundedYear,
                  })}
                </KeyValueDescription>
              )}
            </KeyValueItem>
            <KeyValueItem>
              <KeyValueTitle>
                {i18n.t(`${TRANSLATION_PREFIX}.contact_details`)}
              </KeyValueTitle>
              <KeyValueDescription>
                <a
                  href={`https://${domain.value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {domain.value}
                </a>
              </KeyValueDescription>
              {(domain.city || domain.stateName || domain.countryName) && (
                <KeyValueDescription>
                  {[domain.city, domain.stateName, domain.countryName]
                    .filter(Boolean)
                    .join(", ")}
                </KeyValueDescription>
              )}
              {(domain.twitterUrl ||
                domain.linkedinUrl ||
                domain.facebookUrl ||
                domain.youtubeUrl ||
                domain.instagramUrl) && (
                <KeyValueDescription>
                  {domain.twitterUrl && (
                    <a
                      href={domain.twitterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon icon="twitter" variant="brand" />
                    </a>
                  )}
                  {domain.linkedinUrl && (
                    <a
                      href={domain.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon icon="linkedin" variant="brand" />
                    </a>
                  )}
                  {domain.facebookUrl && (
                    <a
                      href={domain.facebookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon icon="facebook" variant="brand" />
                    </a>
                  )}
                  {domain.youtubeUrl && (
                    <a
                      href={domain.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon icon="youtube" variant="brand" />
                    </a>
                  )}
                  {domain.instagramUrl && (
                    <a
                      href={domain.instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon icon="instagram" variant="brand" />
                    </a>
                  )}
                </KeyValueDescription>
              )}
            </KeyValueItem>
          </KeyValue>

          <TabsList>
            <TabsListItem>
              <TabsTrigger value="email_addresses">
                {i18n.t(`${TRANSLATION_PREFIX}.tabs.email_addresses`)}
              </TabsTrigger>
            </TabsListItem>
            <TabsListItem>
              <TabsTrigger value="signals">
                {i18n.t(`${TRANSLATION_PREFIX}.tabs.signals`)}
              </TabsTrigger>
            </TabsListItem>
            <TabsListItem>
              <TabsTrigger value="technologies">
                {i18n.t(`${TRANSLATION_PREFIX}.tabs.technologies`)}
              </TabsTrigger>
            </TabsListItem>
          </TabsList>
        </section>
        <TabsContent value="email_addresses">
          <DomainSidepanelEmails
            emailIds={undefined}
            numberOfResults={domain.emailsCount}
            genericEmailsCount={domain.numberOfGenericEmailAddresses}
            domain={{
              value: domain.value,
              company_name: domain.companyName,
              company_data: true,
            }}
            numberOfCreditsLeft={numberOfCreditsLeft}
            singleCreditsBucket={singleCreditsBucket}
            leadsLists={leadsLists}
            campaigns={campaigns}
          />
        </TabsContent>
        <TabsContent value="signals">
          <DomainSidepanelSignals domain={domain} />
        </TabsContent>
        <TabsContent value="technologies">
          <DomainSidepanelTechnologies domain={domain} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
